import React from "react";
import Stack from "@mui/material/Stack";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import { ColumnsToolbarButton, ResetLayoutButton } from "@app.automotus.io/components/DataTableContainer";

export interface PermitsTableToolbarProps {
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the export button to icon-only. The filter button's label is controlled separately, at the parent DataGridPro's `localeText` prop. */
  isIconOnly: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
}

export const PermitsTableToolbar: React.FC<PermitsTableToolbarProps> = ({
  fetchTotalCount,
  fetchDataBatch,
  exportFileName,
  columns,
  defaultVisibilityModel,
  isIconOnly,
  resetLayout,
}) => {
  const apiRef = useGridApiContext();
  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="center" height={52} px={1.25} spacing={1}>
      <GridToolbarFilterButton
        componentsProps={{
          button: {
            sx: {
              fontSize: "16px",
              fontWeight: 400,
              letterSpacing: 0.15,
              textTransform: "none",
              ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
            },
          },
        }}
      />
      <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
      <CustomExportButton
        getTotalCount={fetchTotalCount}
        fetchDataBatch={fetchDataBatch}
        fileName={exportFileName}
        columns={columns}
        maxRowsLimit={100000}
        aria-label="Export"
        buttonLabel={isIconOnly ? "" : "Export"}
        sx={{
          fontSize: "16px",
          fontWeight: 400,
          letterSpacing: 0.15,
          textTransform: "none",
          ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
        }}
      />
      <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
    </Stack>
  );
};

export default PermitsTableToolbar;
